import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DataTable from '../../components/tables/DataTable';
import { getStoreAccount } from '../../utils/queries';

export default function StoreAccountDetail() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [account, setAccount] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		setLoading(true);
		setError(null);
		getStoreAccount(id).then((result) => {
			if (result?.success) {
				setAccount(result.data);
			} else {
				setError(result?.error?.message || 'Failed to load store account.');
			}
			setLoading(false);
		});
	}, [id]);

	if (loading) {
		return <div className='py-8 pr-8 text-sm text-gray-400'>Loading...</div>;
	}

	if (!account) {
		return (
			<div className='py-8 pr-8'>
				<p className='text-sm text-gray-500'>{error || 'Store account not found.'}</p>
				<button type='button' className='btn-secondary mt-4' onClick={() => navigate('/store-accounts')}>
					Back to Accounts
				</button>
			</div>
		);
	}

	const orderColumns = [
		{
			key: 'order_number',
			header: 'Order #',
			render: (value, row) => (
				<Link to={`/orders/${row.order_id}`} className='text-black font-medium hover:underline'>
					{value}
				</Link>
			),
		},
		{
			key: 'order_date',
			header: 'Date',
			render: (value) => (value ? new Date(value).toLocaleString() : ''),
		},
		{ key: 'financial_status', header: 'Status' },
		{ key: 'total_price', header: 'Total' },
		{ key: 'erp_order_number', header: 'Pace Order #' },
	];

	const addressColumns = [
		{ key: 'label', header: 'Label', render: (value) => value || '-' },
		{
			key: 'address1',
			header: 'Address',
			render: (value, row) => [value, row.address2].filter(Boolean).join(', '),
		},
		{ key: 'city', header: 'City' },
		{ key: 'provinceCode', header: 'State' },
		{ key: 'zip', header: 'Zip' },
		{ key: 'isDefault', header: 'Default', render: (value) => (value ? 'Yes' : '') },
		{
			key: 'addedDate',
			header: 'Added',
			render: (value) => (value ? new Date(value).toLocaleDateString() : ''),
		},
	];

	return (
		<div className='py-8 pr-8 max-w-3xl'>
			<button
				type='button'
				className='text-sm text-gray-500 hover:text-black mb-2'
				onClick={() => navigate('/store-accounts')}
			>
				&larr; Accounts
			</button>
			<h1 className='text-2xl font-light tracking-tight mb-1'>
				{account.firstName} {account.lastName}
			</h1>
			<p className='text-sm text-gray-500 mb-6'>{account.email}</p>

			<dl className='text-sm space-y-1 mb-8 max-w-md'>
				<div className='flex justify-between'><dt className='text-gray-500'>Phone</dt><dd>{account.phone || '-'}</dd></div>
				<div className='flex justify-between'><dt className='text-gray-500'>Email Verified</dt><dd>{account.emailVerifiedAt ? 'Yes' : 'No'}</dd></div>
				<div className='flex justify-between'>
					<dt className='text-gray-500'>ERP</dt>
					<dd>
						{account.customer ? (
							<Link to={`/customers/${account.customer.customerId}`} className='text-black font-medium hover:underline'>
								{account.customer.firstName} {account.customer.lastName} ({account.customer.erpContactId || account.customer.customerId})
							</Link>
						) : (
							'Not linked yet'
						)}
					</dd>
				</div>
				<div className='flex justify-between'><dt className='text-gray-500'>Created</dt><dd>{account.addedDate ? new Date(account.addedDate).toLocaleString() : '-'}</dd></div>
			</dl>

			<h2 className='text-base font-medium mb-3'>Delivery Addresses</h2>
			<DataTable columns={addressColumns} data={account.addresses} emptyHeading='No saved addresses' />

			<h2 className='text-base font-medium mb-3 mt-8'>Order History</h2>
			<DataTable columns={orderColumns} data={account.orders} emptyHeading='No orders yet' />
		</div>
	);
}
